/**
 * Injects the NuCall loader exactly once per page.
 *
 * The guard is module-scope state, not a ref. A ref survives StrictMode's
 * double-effect on one instance, but a component remount creates a fresh ref
 * and would inject a second copy of the script. Note that StrictMode is off in
 * development and on in production here, so that bug would only ever appear in
 * production — which is why the guard lives at module scope.
 *
 * The script is never removed on logout. Removing a loaded SDK does not un-run
 * it, and re-adding it is not reliably supported; NuCall('shutdown') is the
 * documented teardown, and boot() brings it back.
 */

export interface NuCallApi {
  (method: string, ...args: unknown[]): unknown;
  open: () => void;
  close: () => void;
  toggle: () => void;
  show: () => void;
  hide: () => void;
  shutdown: () => void;
  boot: () => Promise<unknown>;
  identify: (identity: { user_token?: string; user_id?: string }) => Promise<unknown>;
  setIdentity: (identity: { user_token?: string; user_id?: string }) => Promise<unknown>;
  update: (patch: { lang?: string; rtl?: boolean }) => void;
  on: (event: string, fn: (payload?: unknown) => void) => void;
  off: (event: string, fn: (payload?: unknown) => void) => void;
  status: string;
  version: string;
}

declare global {
  interface Window {
    NuCall?: NuCallApi;
  }
}

const LOAD_TIMEOUT_MS = 8000;

let loadPromise: Promise<NuCallApi> | null = null;

export interface LoadOptions {
  src: string;
  integrity?: string;
  widgetKey: string;
  identityToken?: string;
  position?: 'left' | 'right';
  lang?: string;
}

export function loadNuCall(opts: LoadOptions): Promise<NuCallApi> {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<NuCallApi>((resolve, reject) => {
    if (typeof document === 'undefined') return reject(new Error('no-document'));

    const existing = document.querySelector<HTMLScriptElement>('script[data-nucall-loader]');
    if (existing && window.NuCall) return resolve(window.NuCall);

    const s = document.createElement('script');
    s.src = opts.src;
    s.async = true;
    s.dataset.nucallLoader = '1';
    // SRI: the browser refuses the file if a single byte changed, so NuCall
    // cannot push new JavaScript into our origin without us deploying first.
    // crossOrigin is required for integrity to be enforced at all.
    if (opts.integrity) {
      s.integrity = opts.integrity;
      s.crossOrigin = 'anonymous';
    }
    // Our URLs can carry capability tokens (e.g. /public/proposals/:token);
    // NuCall only needs to know which site we are.
    s.referrerPolicy = 'strict-origin-when-cross-origin';

    s.dataset.key = opts.widgetKey;
    if (opts.identityToken) s.dataset.userToken = opts.identityToken;
    if (opts.position) s.dataset.position = opts.position;
    if (opts.lang) s.dataset.lang = opts.lang;

    const timer = window.setTimeout(() => {
      loadPromise = null; // allow a later retry
      reject(new Error('nucall-timeout'));
    }, LOAD_TIMEOUT_MS);

    s.onload = () => {
      window.clearTimeout(timer);
      if (window.NuCall) resolve(window.NuCall);
      else {
        loadPromise = null;
        reject(new Error('nucall-missing-global'));
      }
    };
    s.onerror = () => {
      window.clearTimeout(timer);
      loadPromise = null; // network blip or SRI mismatch — retry is allowed
      reject(new Error('nucall-load-failed'));
    };

    document.body.appendChild(s);
  });

  return loadPromise;
}

/** Test-only: forget that the script was loaded. */
export function __resetNuCallLoaderForTests() {
  loadPromise = null;
}
